import type { Profile } from "../types/index.ts";

// Mirrors the raw-REST pattern used by TipRoundService / TournamentService:
// the bundled supabase v2.0.0 client hangs on .from() calls in this
// environment, so we drive PostgREST directly. Profile loads were previously
// using the broken client path in App.tsx — symptom was a null `state.profile`
// even though the row existed in DB, falling back to the email-prefix in the
// header pill.

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";
const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (
      parsed?.access_token ?? parsed?.currentSession?.access_token ?? null
    );
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => {
  const token =
    getAccessTokenFromStorage() ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

// Default name written by the legacy Supabase Studio profile-row default.
// Treated as "unset" for the purpose of nudging the user to pick a real name.
export const DEFAULT_DISPLAY_NAME = "Neuer Spieler";

export const needsRealDisplayName = (profile: Profile | null): boolean => {
  const name = profile?.display_name?.trim();
  return !name || name === DEFAULT_DISPLAY_NAME;
};

class ProfileService {
  async getProfile(userId: string): Promise<Profile | null> {
    try {
      // Use the user's JWT instead of anon — if the profiles table has RLS
      // requiring `auth.uid() = user_id`, anon reads return an empty array
      // silently. handleLogin always calls this AFTER the session is
      // restored, so the token is in localStorage by then.
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?user_id=eq.${userId}&select=*`,
        { method: "GET", headers: authedHeaders() },
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as Profile[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("ProfileService.getProfile failed:", e);
      return null;
    }
  }

  async createIfMissing(userId: string): Promise<Profile | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?on_conflict=user_id`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(),
            Prefer: "resolution=merge-duplicates,return=representation",
          },
          body: JSON.stringify({ user_id: userId }),
        },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("ProfileService.createIfMissing failed:", res.status, body);
        return null;
      }
      const rows = (await res.json()) as Profile[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("ProfileService.createIfMissing error:", e);
      return null;
    }
  }

  async getOrCreate(userId: string): Promise<Profile | null> {
    const existing = await this.getProfile(userId);
    if (existing) return existing;
    return await this.createIfMissing(userId);
  }

  async updateDisplayName(
    userId: string,
    displayName: string,
  ): Promise<Profile | null> {
    return await this.updateProfile(userId, {
      display_name: displayName.trim(),
    });
  }

  /**
   * Generic profile patch — pass only the fields you want to change.
   * Used by ProfileEditModal which lets the user edit display_name and
   * country_code in one save.
   */
  async updateProfile(
    userId: string,
    patch: Partial<Pick<Profile, "display_name" | "country_code">>,
  ): Promise<Profile | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?user_id=eq.${userId}`,
        {
          method: "PATCH",
          headers: {
            ...authedHeaders(),
            Prefer: "return=representation",
          },
          body: JSON.stringify(patch),
        },
      );
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("ProfileService.updateProfile failed:", res.status, body);
        return null;
      }
      const rows = (await res.json()) as Profile[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("ProfileService.updateProfile error:", e);
      return null;
    }
  }
}

export const profileService = new ProfileService();
